
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaFileExcel, FaFilePdf, FaTimes } from "react-icons/fa";
import { format, subDays } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import "./alertReport.css";

const API_BASE_URL = "https://api.yellowmatrix.co.in";

// ─── Alert Type Config ────────────────────────────────────────────────────────

const ALERT_TYPES = [
  { label: "All",                  value: "All" },
  { label: "Overspeed Alert",      value: "overspeed" },
  { label: "Harsh Braking Alert",  value: "harsh_breaking" },
  { label: "Harsh Acceleration Alert", value: "harsh_acceleration" },
  { label: "Sharp Turn Alert",     value: "sharp_turn" },
  { label: "Long Idling Alert",    value: "long_idling" },
  { label: "Geofence Alert",       value: "geofence" },
  { label: "Panic Button Alert",   value: "panic_button" },
  { label: "Device Tamper Alert",  value: "device_tamper" },
  { label: "Device Unplugged Alert", value: "device_unplugged" },
  { label: "DTC Alert",            value: "dtc_alert" },
  { label: "Analog 1 Alert",       value: "analog_1" },
  { label: "Analog 2 Alert",       value: "analog_2" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateForAPI = (dateStr) => {
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
};

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr || dateTimeStr === "null") return "—";
  const d = new Date(dateTimeStr);
  if (isNaN(d)) return dateTimeStr;
  return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
};

const displayVal = (val) =>
  val === null || val === undefined || val === "null" || val === "" ? "—" : val;

const cleanDTCCodes = (codes) => {
  if (!codes || codes === "null") return "—";
  return (
    codes
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c && c !== "000")
      .join(", ") || "—"
  );
};

// ─── Custom Hook: Fetch available vehicles ────────────────────────────────────

const useAvailableVehiclesQuery = () => {
  const fetchAvailableVehicles = async () => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Authentication token not found. Please log in.");

    const response = await fetch(`${API_BASE_URL}/vehicles`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.Error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.map((v) => v.VehicleID);
  };

  return useQuery({
    queryKey: ["availableVehicles"],
    queryFn: fetchAvailableVehicles,
    staleTime: Infinity,
  });
};

// ─── Custom Hook: Fetch alert report data ────────────────────────────────────

const useAlertDataQuery = (
  selectedVehicles,
  startDate,
  endDate,
  alertType,
  page,
  perPage,
  isEnabled
) => {
  const fetchAlertData = async () => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Authentication token not found");
    if (selectedVehicles.length === 0) throw new Error("No vehicles selected");

    const params = new URLSearchParams();
    params.append("vehicle_id", selectedVehicles.join(","));
    params.append("start_date", formatDateForAPI(startDate));
    params.append("end_date", formatDateForAPI(endDate));
    params.append("page", page);
    params.append("per_page", perPage);
    if (alertType && alertType !== "All") params.append("alert_type", alertType);

    const response = await fetch(
      `${API_BASE_URL}/alerts-report?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.Error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  return useQuery({
    queryKey: [
      "alertReport",
      selectedVehicles,
      startDate,
      endDate,
      alertType,
      page,
      perPage,
    ],
    queryFn: fetchAlertData,
    enabled: isEnabled && selectedVehicles.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

// ─── Skeleton Row ─────────────────────────────────────────────────────────────

const SkeletonRow = ({ columns }) => (
  <tr className="skeleton-row-alert-report">
    {[...Array(columns)].map((_, i) => (
      <td key={i}>
        <div
          className="skeleton-line-alert-report"
          style={{ width: `${Math.floor(Math.random() * 31) + 60}%` }}
        />
      </td>
    ))}
  </tr>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AlertReport = () => {
  // Filter states
  const [searchInput, setSearchInput] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [alertType, setAlertType] = useState("All");
  const [dateRange, setDateRange] = useState("Last Day");
  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 1), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  // Report control states
  const [reportTriggered, setReportTriggered] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 25;

  // Autocomplete
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef(null);

  // ── Queries ────────────────────────────────────────────────────────────────

  const {
    data: availableVehicles = [],
    isLoading: fetchingVehicles,
    error: vehicleFetchError,
  } = useAvailableVehiclesQuery();

  const {
    data: alertResponse,
    isLoading: isAlertLoading,
    isRefetching: isAlertRefetching,
    error: alertFetchError,
  } = useAlertDataQuery(
    selectedVehicles,
    startDate,
    endDate,
    alertType,
    page,
    perPage,
    reportTriggered
  );

  const alertData = alertResponse?.data || [];
  const totalPages = alertResponse?.pages || 0;
  const totalRecords = alertResponse?.total || 0;

  const loading = isAlertLoading || isAlertRefetching;
  const hasData = alertData.length > 0;
  const error =
    alertFetchError?.message || vehicleFetchError?.message || downloadError;

  // ── Autocomplete ──────────────────────────────────────────────────────────

  useEffect(() => {
    const filtered =
      searchInput.length > 0
        ? availableVehicles.filter((v) =>
            v.toLowerCase().includes(searchInput.toLowerCase())
          )
        : availableVehicles;
    setFilteredSuggestions(filtered);
  }, [searchInput, availableVehicles]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
    setShowSuggestions(true);
  };

  const onSuggestionClick = (vehicleId) => {
    setSearchInput("");
    setFilteredSuggestions([]);
    setShowSuggestions(false);
    if (!selectedVehicles.includes(vehicleId)) {
      setSelectedVehicles([...selectedVehicles, vehicleId]);
    }
  };

  const removeVehicle = (vehicle) => {
    setSelectedVehicles(selectedVehicles.filter((v) => v !== vehicle));
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    setDateRange(value);
    const today = new Date();
    const dateMap = { "Last Day": 1, "Last 7 Days": 7 };
    if (dateMap[value] !== undefined) {
      setStartDate(format(subDays(today, dateMap[value]), "yyyy-MM-dd"));
      setEndDate(format(today, "yyyy-MM-dd"));
    }
  };

  const handleAlertTypeChange = (e) => {
    setAlertType(e.target.value);
    // Reset to page 1 when filter changes
    setPage(1);
    // Re-trigger report if already generated
    if (reportTriggered) {
      setReportTriggered(false);
      setTimeout(() => setReportTriggered(true), 0);
    }
  };

  const handleReset = () => {
    setSearchInput("");
    setSelectedVehicles([]);
    setAlertType("All");
    setDateRange("Last Day");
    setStartDate(format(subDays(new Date(), 1), "yyyy-MM-dd"));
    setEndDate(format(new Date(), "yyyy-MM-dd"));
    setReportTriggered(false);
    setDownloadError(null);
    setPage(1);
  };

  const handleGenerateReport = () => {
    if (selectedVehicles.length === 0) return;
    setPage(1);
    setReportTriggered(true);
  };

  // ── Download ──────────────────────────────────────────────────────────────

  const handleDownload = useCallback(
    async (fileType) => {
      if (selectedVehicles.length === 0) {
        setDownloadError(
          "Please select at least one vehicle to download report"
        );
        return;
      }
      if (!hasData) {
        setDownloadError("No data available to download");
        return;
      }

      setDownloadError(null);

      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Authentication token not found");

        const params = new URLSearchParams();
        params.append("vehicle_id", selectedVehicles.join(","));
        params.append("start_date", formatDateForAPI(startDate));
        params.append("end_date", formatDateForAPI(endDate));
        params.append("download", fileType);
        if (alertType && alertType !== "All")
          params.append("alert_type", alertType);

        const response = await fetch(
          `${API_BASE_URL}/alerts-report?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            errorText || `Download failed with status ${response.status}`
          );
        }

        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = `alerts_report_${format(
          new Date(),
          "yyyyMMdd_HHmmss"
        )}.${fileType}`;
        if (contentDisposition) {
          const match = contentDisposition.match(
            /filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/i
          );
          if (match?.[1]) filename = decodeURIComponent(match[1]);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement("a");
        downloadLink.href = blobUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(downloadLink);
        }, 100);
      } catch (err) {
        setDownloadError(err.message || "Failed to download report");
        console.error("Download error:", err);
      }
    },
    [selectedVehicles, startDate, endDate, alertType, hasData]
  );

  // ── Table Columns based on alert type ─────────────────────────────────────

  const isDTC = alertType === "dtc_alert";
  const isAll = alertType === "All";

  // ── Table Render ──────────────────────────────────────────────────────────

  const renderTableBody = () => {
    if (loading && reportTriggered) {
      return [...Array(10)].map((_, i) => (
        <SkeletonRow key={i} columns={isDTC ? 5 : 9} />
      ));
    }

    if (hasData) {
      return alertData.map((row, index) => (
        <tr key={index}>
          <td>{displayVal(row.VehicleID)}</td>
          <td>{displayVal(row.CustomerID)}</td>
          <td>{displayVal(row.DeviceID)}</td>
          <td>
            <span
              className={`alert-type-text alert-type-text--${(row.AlertType || "")
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              {displayVal(row.AlertType)}
            </span>
          </td>

          {/* Show threshold/value/speed/location only for non-DTC rows */}
          {(isAll || !isDTC) && (
            <>
              <td>{displayVal(row.Threshold)}</td>
              <td>{displayVal(row.Speed)}</td>
              <td>
                {row.Latitude === "null" ||
                !row.Latitude ||
                row.Longitude === "null" ||
                !row.Longitude
                  ? "—"
                  : `${row.Latitude}, ${row.Longitude}`}
              </td>
            </>
          )}

          {/* DTC codes — always show for All or dtc_alert */}
          {(isAll || isDTC) && (
            <td className="dtc-codes-cell">{cleanDTCCodes(row.DTC_CODES)}</td>
          )}

          <td>{formatDateTime(row.Timestamp)}</td>
        </tr>
      ));
    }

    return (
      <tr>
        <td colSpan={isDTC ? 5 : 9} className="no-data-cell">
          {!reportTriggered
            ? "Select vehicles and click GENERATE REPORT to view data"
            : selectedVehicles.length === 0
            ? "Please select vehicles to generate report"
            : "No data available for the selected filters"}
        </td>
      </tr>
    );
  };

  // ── Dynamic table headers ──────────────────────────────────────────────────

  const renderTableHeaders = () => {
    return (
      <tr>
        <th className="table-heading">Vehicle ID</th>
        <th className="table-heading">Customer ID</th>
        <th className="table-heading">Device ID</th>
        <th className="table-heading">Alert Type</th>
        {(isAll || !isDTC) && (
          <>
            <th className="table-heading">Threshold</th>
            <th className="table-heading">Speed</th>
            <th className="table-heading">Location</th>
          </>
        )}
        {(isAll || isDTC) && (
          <th className="table-heading">DTC Codes</th>
        )}
        <th className="table-heading">Timestamp</th>
      </tr>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="alert-report-container">
      <h1 className="report-title">Alert Report</h1>
      <hr className="alert-report-line" width="100%" />

      {/* ── Controls ── */}
      <div className="controls-container">

        {/* Vehicle Search */}
        <div className="search-container-alert" ref={searchInputRef}>
          <FaSearch className="search-icon-alert" />
          <input
            type="text"
            placeholder="Search Vehicle ID..."
            className="search-input-alert"
            value={searchInput}
            onChange={handleSearchInputChange}
            onFocus={() => {
              setFilteredSuggestions(availableVehicles);
              setShowSuggestions(true);
            }}
          />
          {showSuggestions && filteredSuggestions.length > 0 && (
            <ul className="suggestions-list-alert">
              {filteredSuggestions.map((vehicleId, index) => (
                <li
                  key={index}
                  onClick={() => onSuggestionClick(vehicleId)}
                  className="suggestion-item-alert"
                >
                  {vehicleId}
                </li>
              ))}
            </ul>
          )}
          {fetchingVehicles && (
            <div className="loading-suggestions-alert">Loading vehicles...</div>
          )}
          {vehicleFetchError && (
            <div className="error-message-small-alert">
              Error: {vehicleFetchError.message}
            </div>
          )}
        </div>

        {/* Alert Type — updated with all 12 types */}
        <div className="date-range-container">
          <label className="control-label">Alert Type</label>
          <select
            className="date-range-select"
            value={alertType}
            onChange={handleAlertTypeChange}
          >
            {ALERT_TYPES.map((at) => (
              <option key={at.value} value={at.value}>
                {at.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range */}
        <div className="date-range-container">
          <label className="control-label">Date Range</label>
          <select
            value={dateRange}
            onChange={handleDateRangeChange}
            className="date-range-select"
          >
            <option>Last Day</option>
            <option>Last 7 Days</option>
            <option>Custom</option>
          </select>
        </div>

        {/* Start Date */}
        <div className="date-input-container">
          <label className="control-label">Start Date:</label>
          <input
            type="date"
            className="date-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={dateRange !== "Custom"}
          />
        </div>

        {/* End Date */}
        <div className="date-input-container">
          <label className="control-label">End Date:</label>
          <input
            type="date"
            className="date-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={dateRange !== "Custom"}
          />
        </div>

        {/* Action Buttons */}
        <button
          className="generate-report-btn"
          onClick={handleGenerateReport}
          disabled={loading || selectedVehicles.length === 0}
        >
          {loading ? "LOADING..." : "GENERATE REPORT"}
        </button>

        <button className="action-btn-alert reset-btn" onClick={handleReset}>
          RESET
        </button>

        <Link to="/Report">
          <button className="action-btn-alert back-btn">
            BACK TO ALL REPORTS
          </button>
        </Link>

        {/* Download Icons */}
        <FaFileExcel
          className={`excel-icon file-icon ${
            loading || !hasData ? "disabled-icon" : ""
          }`}
          title={
            hasData
              ? "Download Excel Report"
              : "No data available to download"
          }
          onClick={() => !loading && hasData && handleDownload("xlsx")}
        />
        <FaFilePdf
          className={`pdf-icon file-icon ${
            loading || !hasData ? "disabled-icon" : ""
          }`}
          title={
            hasData ? "Download PDF Report" : "No data available to download"
          }
          onClick={() => !loading && hasData && handleDownload("pdf")}
        />
      </div>

      {/* ── Selected Vehicles ── */}
      <div className="selected-vehicles-container">
        {selectedVehicles.length > 0 ? (
          selectedVehicles.map((vehicle, idx) => (
            <span key={idx} className="vehicle-tag">
              {vehicle}
              <FaTimes
                className="remove-icon"
                onClick={() => !loading && removeVehicle(vehicle)}
                title="Remove vehicle"
              />
            </span>
          ))
        ) : (
          <div className="no-vehicles-selected">No vehicles selected</div>
        )}
      </div>

      {/* ── Error Message ── */}
      {error && (
        <div className="error-message">
          Error: {error}
          {error.includes("Authentication token not found") && (
            <button
              onClick={() => (window.location.href = "/login")}
              className="login-redirect-btn"
            >
              Go to Login
            </button>
          )}
        </div>
      )}

      {/* ── Active Filter Badge ── */}
      {reportTriggered && alertType !== "All" && (
        <div className="active-filter-badge">
          Filtered by:{" "}
          <strong>
            {ALERT_TYPES.find((a) => a.value === alertType)?.label || alertType}
          </strong>
          <button
            className="clear-filter-btn"
            onClick={() => {
              setAlertType("All");
              setPage(1);
              if (reportTriggered) {
                setReportTriggered(false);
                setTimeout(() => setReportTriggered(true), 0);
              }
            }}
          >
            ✕ Clear
          </button>
        </div>
      )}

      {/* ── Summary Bar ── */}
      {reportTriggered && !loading && totalRecords > 0 && (
        <div className="report-summary-bar">
          Showing <strong>{alertData.length}</strong> of{" "}
          <strong>{totalRecords}</strong> records &nbsp;|&nbsp; Page{" "}
          <strong>{page}</strong> of <strong>{totalPages}</strong>
        </div>
      )}

      {/* ── Table ── */}
      <div className="table-container-alert">
        <table className="report-table-alert">
          <thead className="table-header">{renderTableHeaders()}</thead>
          <tbody>{renderTableBody()}</tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {reportTriggered && totalPages > 1 && (
        <div className="pagination-container-alert">
          <button
            className="pagination-btn-alert"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            ← Prev
          </button>

          <span className="pagination-info-alert">
            Page {page} / {totalPages}
          </span>

          <button
            className="pagination-btn-alert"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default AlertReport;